// MorrowOS "connect an app" client helper (Composio).
//
// The first-win landing lets a user connect Gmail / Calendar / Slack / Notion /
// GitHub and then chat against them. Connecting an app is a per-user, persisted
// integration, so every call is scoped to a stable `userId`:
//   - if the user is signed in (Clerk), we use their Clerk user id;
//   - otherwise we mint + persist a guest id in localStorage so the same
//     anonymous browser keeps its connections across reloads.
//
// The two endpoints below are built by the server (contract only here). During
// local dev they may 404 before the server side lands — callers should treat a
// failed fetch as "nothing connected yet" and keep the UI working.

import { apiUrl } from "./apiBase";
import { getClerkToken } from "./clerkTokenBridge";
import { openUpgradeModal, parseGateUpgrade } from "./upgradeModal";

/** The five toolkits the landing can connect. */
export const MORROW_TOOLKITS = ["gmail", "googlecalendar", "slack", "notion", "github"] as const;
export type MorrowToolkit = (typeof MORROW_TOOLKITS)[number];

const GUEST_ID_KEY = "morrow-guest-id";

// Cookie the server reads to scope chats to this browser's owner. It rides on
// the same-origin WebSocket upgrade and HTTP snapshot requests, so the server
// can isolate each user's threads without a bespoke handshake. Must match the
// server's OWNER_ID_COOKIE_NAME (apps/server/src/orchestration/ThreadOwnership.ts).
const OWNER_ID_COOKIE_NAME = "morrow_owner_id";

/**
 * Persist the resolved owner id as a first-party cookie so the server can scope
 * thread listing/access to this browser. Best-effort: cookies can be blocked in
 * private/sandboxed contexts, in which case chat isolation still holds for the
 * WebSocket via the `?ownerId=` fallback the connection appends.
 *
 * Uses `SameSite=Lax` + `path=/` so it accompanies same-origin navigations and
 * the WS upgrade. A long max-age keeps a guest's isolation stable across
 * reloads (the guest id itself is already persisted in localStorage).
 */
function persistOwnerIdCookie(ownerId: string): void {
  try {
    if (typeof document === "undefined") return;
    const encoded = encodeURIComponent(ownerId);
    const oneYearSeconds = 365 * 24 * 60 * 60;
    document.cookie = `${OWNER_ID_COOKIE_NAME}=${encoded}; path=/; max-age=${oneYearSeconds}; SameSite=Lax`;
  } catch {
    // Best-effort only.
  }
}

// The owner id the WebSocket connection was established with at page boot (before
// Clerk resolves). Captured once so a later sign-in can detect that the live WS
// is stale (opened under a guest id) and re-open it under the Clerk owner.
let wsBootOwnerId: string | null = null;

/** Record (once) the owner id the initial WebSocket connection used. */
export function markWsBootOwner(id: string): void {
  if (wsBootOwnerId === null) wsBootOwnerId = id;
}

/** The owner id the live WebSocket connection was established with. */
export function getWsBootOwnerId(): string | null {
  return wsBootOwnerId;
}

/** Read the current `morrow_owner_id` cookie value, if any. */
function readOwnerIdCookie(): string | null {
  try {
    if (typeof document === "undefined") return null;
    const prefix = `${OWNER_ID_COOKIE_NAME}=`;
    const match = document.cookie.split("; ").find((c) => c.startsWith(prefix));
    if (!match) return null;
    const value = decodeURIComponent(match.slice(prefix.length));
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    // localStorage can throw in privacy modes / sandboxed frames.
    return null;
  }
}

function randomGuestId(): string {
  // A guest id is a best-effort per-browser nudge (like guestAuth's counter),
  // not a security boundary — a cheap random token is plenty and avoids pulling
  // in Effect's Random module for a throwaway id.
  const rand = `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `guest_${Date.now().toString(36)}${rand}`;
}

/**
 * Resolve a stable per-user id for Composio calls. Pass the Clerk `userId` when
 * signed in; otherwise a guest id is created (once) and persisted in
 * localStorage so it survives reloads.
 */
export function resolveComposioUserId(clerkUserId: string | null | undefined): string {
  if (clerkUserId) {
    // Same id also scopes chat ownership on the server (see persistOwnerIdCookie).
    persistOwnerIdCookie(clerkUserId);
    return clerkUserId;
  }
  // Signed-out, OR Clerk hasn't loaded yet (the synchronous boot call in
  // main.tsx). Do NOT clobber a persisted Clerk owner cookie from a previous
  // session with a guest id — otherwise the WebSocket opens under the guest owner
  // before Clerk resolves, and every thread this session creates is stamped to
  // the connectionless guest id (stranding the signed-in user's chats + Composio
  // connections). Keep the persisted Clerk id if the cookie already holds one.
  const persisted = readOwnerIdCookie();
  if (persisted && persisted.startsWith("user_")) {
    return persisted;
  }
  const storage = safeStorage();
  if (!storage) {
    const ephemeral = randomGuestId();
    persistOwnerIdCookie(ephemeral);
    return ephemeral;
  }
  let id = storage.getItem(GUEST_ID_KEY);
  if (!id) {
    id = randomGuestId();
    try {
      storage.setItem(GUEST_ID_KEY, id);
    } catch {
      // Best-effort; if we can't persist, the caller still gets a usable id.
    }
  }
  persistOwnerIdCookie(id);
  return id;
}

/**
 * Guest id already persisted for this browser, if any. Does not mint a new one.
 * Sent as `x-morrow-claim-guest-id` after sign-in so chats created under the
 * guest owner can be re-homed to the Clerk user instead of vanishing on refresh.
 */
export function getPersistedGuestId(): string | null {
  const id = safeStorage()?.getItem(GUEST_ID_KEY);
  return id && id.startsWith("guest_") ? id : null;
}

/**
 * The stable per-browser guest owner id, minting + persisting one if needed —
 * and **overwriting any lingering `user_` owner cookie**. Unlike
 * {@link resolveComposioUserId}, this never keeps a persisted `user_` id.
 *
 * Call this only once Clerk has finished loading AND confirmed there is no
 * signed-in user. That's the "returning visitor whose Clerk session lapsed"
 * case: a `user_` owner cookie survives from the previous sign-in, but with no
 * valid Clerk token the backend rejects a `user_` owner id (`authenticated:
 * false`) and the auth gate then dumps the visitor on `/pair` behind a sign-in
 * wall. Falling back to the guest id keeps guest-first working (the backend
 * mints an `authenticated` guest session for a `guest_` owner id).
 */
export function ensureGuestOwnerId(): string {
  const storage = safeStorage();
  if (!storage) {
    const ephemeral = randomGuestId();
    persistOwnerIdCookie(ephemeral);
    return ephemeral;
  }
  let id = storage.getItem(GUEST_ID_KEY);
  if (!id || !id.startsWith("guest_")) {
    id = randomGuestId();
    try {
      storage.setItem(GUEST_ID_KEY, id);
    } catch {
      // Best-effort; if we can't persist, the caller still gets a usable id.
    }
  }
  persistOwnerIdCookie(id);
  return id;
}

/**
 * Fetch every connected toolkit slug for a user (not limited to the landing five).
 * GET /api/morrow/composio/connections?userId=<uid> -> { connected: string[] }
 *
 * Returns `null` for "UNKNOWN — don't touch what's shown", vs an array for an
 * authoritative answer (`[]` = confirmed zero). Null is returned when:
 *   - the id is not a real signed-in user (`user_…`) — a `guest_…`/empty id has no
 *     connections, and querying it would wrongly blank out a signed-in user's apps
 *     when Clerk hasn't confirmed sign-in yet, or a lapsed session degraded the id;
 *   - the request fails / is malformed — a transient Composio hiccup (common right
 *     after a deploy/cold container) must NOT be rendered as "all disconnected".
 * Callers MUST keep their previous set on null and only replace it on an array.
 * Connections live in Composio keyed by this id and persist; they only *look*
 * gone when we query the wrong id or swallow an error — this prevents both.
 */
export async function fetchConnectedSlugs(userId: string): Promise<string[] | null> {
  if (!userId || !userId.startsWith("user_")) return null;
  try {
    const res = await fetch(
      apiUrl(`/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`),
      { headers: { accept: "application/json" } },
    );
    if (!res.ok) {
      console.warn(`[composio] connections fetch failed: ${res.status}`);
      return null;
    }
    const data = (await res.json()) as { connected?: unknown };
    if (!Array.isArray(data.connected)) return null;
    return data.connected.filter((t): t is string => typeof t === "string" && t.length > 0);
  } catch (err) {
    console.warn("[composio] connections fetch error", err);
    return null;
  }
}

/**
 * Fetch the set of connected toolkits for a user, filtered to the landing five.
 */
export async function fetchConnections(userId: string): Promise<MorrowToolkit[]> {
  const known = new Set<string>(MORROW_TOOLKITS);
  return ((await fetchConnectedSlugs(userId)) ?? []).filter((t): t is MorrowToolkit =>
    known.has(t),
  );
}

/**
 * The five recommended-toolkit "profiles" keyed off the questionnaire answers.
 * Each is a hand-picked set of five high-signal Composio slugs. The default set
 * leads with the highest-information apps (mail, calendar, drive) that help
 * almost anyone. Every slug here appears in the server's FEATURED list so the
 * connector browser and card metadata already know it.
 */
const RECOMMENDED_TOOLKITS = {
  dev: ["github", "linear", "jira", "gmail", "googlecalendar"],
  sales: ["hubspot", "gmail", "googlecalendar", "slack", "notion"],
  design: ["figma", "notion", "slack", "gmail", "googledrive"],
  default: ["gmail", "googlecalendar", "googledrive", "slack", "notion"],
} as const satisfies Record<string, readonly string[]>;

/** Ordered keyword → profile rules; the first matching rule wins. */
const RECOMMENDATION_RULES: ReadonlyArray<{
  readonly profile: keyof typeof RECOMMENDED_TOOLKITS;
  readonly keywords: readonly string[];
}> = [
  {
    profile: "dev",
    keywords: [
      "develop",
      "engineer",
      "software",
      "programmer",
      "devops",
      "sre",
      "coding",
      "backend",
      "frontend",
      "fullstack",
      "technical",
      "cto",
      "it ",
    ],
  },
  {
    profile: "design",
    keywords: ["design", "ux", "ui", "product design", "creative", "brand", "graphic", "figma"],
  },
  {
    profile: "sales",
    keywords: [
      "sales",
      "market",
      "growth",
      "revenue",
      "account executive",
      "business development",
      "bd",
      "crm",
      "hubspot",
      "founder",
      "ceo",
    ],
  },
];

/**
 * Recommend five toolkit slugs to surface in the onboarding connect grid, based
 * on the questionnaire's role + industry. Pure and deterministic: we lowercase +
 * concatenate the two free-text answers, match them against ordered keyword
 * rules, and fall back to a general-purpose set when nothing matches.
 */
export function recommendToolkits(answers: {
  readonly role?: string | undefined;
  readonly industry?: string | undefined;
}): string[] {
  const haystack = `${answers.role ?? ""} ${answers.industry ?? ""}`.toLowerCase();
  const match = RECOMMENDATION_RULES.find((rule) =>
    rule.keywords.some((keyword) => haystack.includes(keyword)),
  );
  return [...RECOMMENDED_TOOLKITS[match?.profile ?? "default"]];
}

/**
 * Outcome of a connect attempt:
 *  - `redirect`  — an OAuth toolkit; open `url` for the user to authorize.
 *  - `connected` — a no-auth toolkit (or one already connected); usable now, no
 *                  popup needed. Server returns `{ connected: true }`.
 *  - `failed`    — the request errored; surface it to the user.
 */
export type ConnectOutcome =
  | { readonly kind: "redirect"; readonly url: string }
  | { readonly kind: "connected" }
  | { readonly kind: "failed" };

/** Parse a /connect response body into a {@link ConnectOutcome}. */
export function connectOutcomeFrom(data: unknown): ConnectOutcome {
  const rec = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  if (typeof rec.redirectUrl === "string" && rec.redirectUrl.length > 0) {
    return { kind: "redirect", url: rec.redirectUrl };
  }
  if (rec.connected === true) {
    return { kind: "connected" };
  }
  return { kind: "failed" };
}

/**
 * Begin connecting a toolkit.
 * POST /api/morrow/composio/connect { toolkit, userId }
 *   -> { redirectUrl }      (OAuth toolkit)
 *   -> { connected: true }  (no-auth toolkit / already connected)
 *
 * Accepts any toolkit slug (not just the landing five) so the recommended-app
 * grid can drive it too. Never throws.
 */
export async function startConnect(toolkit: string, userId: string): Promise<ConnectOutcome> {
  try {
    // Attach the Clerk token so the server can resolve the caller's active ORG
    // (verified, never trusted from the body) — that's whose plan the connector
    // entitlement is checked against. Without it, a business member on biz-max is
    // checked as an individual and wrongly walled.
    const token = await getClerkToken().catch(() => null);
    const headers: Record<string, string> = {
      "content-type": "application/json",
      accept: "application/json",
    };
    if (token) headers["x-clerk-token"] = token;
    const res = await fetch(apiUrl("/api/morrow/composio/connect"), {
      method: "POST",
      headers,
      body: JSON.stringify({ toolkit, userId }),
    });
    // Plan gate: this connector isn't in the plan's set / over the count →
    // surface the upgrade modal (message carries the capability, encoded).
    if (res.status === 402) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      const gate = parseGateUpgrade(body?.error);
      openUpgradeModal(
        gate
          ? { capability: gate.capability, message: gate.message, reason: gate.reason }
          : { capability: "tools.catalog.full" },
      );
      return { kind: "failed" };
    }
    if (!res.ok) {
      console.warn(`[composio] connect failed: ${res.status}`);
      return { kind: "failed" };
    }
    return connectOutcomeFrom(await res.json());
  } catch (err) {
    console.warn("[composio] connect error", err);
    return { kind: "failed" };
  }
}

/**
 * Disconnect a toolkit for a user (one-click).
 * POST /api/morrow/composio/disconnect { toolkit, userId } -> { disconnected }
 *
 * Deletes the user's connected account(s) for the toolkit server-side. Returns
 * true on success, false on failure (warns). Never throws — callers refresh the
 * connection list afterward regardless.
 */
export async function startDisconnect(userId: string, toolkit: string): Promise<boolean> {
  try {
    const res = await fetch(apiUrl("/api/morrow/composio/disconnect"), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ toolkit, userId }),
    });
    if (!res.ok) {
      console.warn(`[composio] disconnect failed: ${res.status}`);
      return false;
    }
    return true;
  } catch (err) {
    console.warn("[composio] disconnect error", err);
    return false;
  }
}

/** Open a centered popup for the OAuth redirect. Returns the window (or null). */
export function openConnectPopup(url: string): Window | null {
  try {
    const w = 520;
    const h = 640;
    const left = typeof window !== "undefined" ? window.screenX + (window.outerWidth - w) / 2 : 0;
    const top = typeof window !== "undefined" ? window.screenY + (window.outerHeight - h) / 2 : 0;
    return window.open(
      url,
      "morrow-composio-connect",
      `width=${w},height=${h},left=${Math.max(0, left)},top=${Math.max(0, top)}`,
    );
  } catch (err) {
    console.warn("[composio] popup open error", err);
    return null;
  }
}

/**
 * Open the OAuth popup *synchronously* inside the click handler — before the
 * redirect URL is known — so the browser's popup blocker (which only trusts
 * `window.open` calls in the direct user-gesture stack) allows it. The window
 * shows a blank placeholder until `pointConnectPopup` sends it to the real URL
 * once `startConnect` resolves. Returns the window (or null if blocked).
 */
export function openPendingConnectPopup(): Window | null {
  return openConnectPopup("about:blank");
}

/**
 * Send a previously-opened pending popup to the resolved OAuth URL. Falls back
 * to a fresh `window.open` if the pending window was blocked or closed (that
 * fallback may itself be blocked post-await, but it is the best we can do).
 */
export function pointConnectPopup(popup: Window | null, url: string): Window | null {
  if (popup && !popup.closed) {
    try {
      popup.location.href = url;
      return popup;
    } catch {
      // Cross-origin or detached; fall through to a fresh open.
    }
  }
  return openConnectPopup(url);
}
